<script lang="ts" setup>
  import { computed } from 'vue';
  import { type Toast, ToastKind } from '@othercommerce/foundation';

  interface Props {
    position: number,
    toast: Toast,
  }

  const props = defineProps<Props>();
  const duration = computed(() => props.toast.duration + 's');

  const active = computed(() => {
    return props.position === 0 && props.toast.kind === ToastKind.SUCCESS;
  });
</script>
<template>
  <Toast v-slot="{ close, closeAll }" :class="toast.kind" :position="position" :toast="toast" class="toast-wrapper">
    <div :class="{ active }" class="toast-container">
      <div :class="toast.kind" class="toast">
        <div class="icon">
          <div class="inside">
            <svg v-if="toast.kind === ToastKind.SUCCESS" aria-hidden="true" fill="currentColor" height="1em" viewBox="0 0 16 16" width="1em" xmlns="http://www.w3.org/2000/svg">
              <path d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425z" />
            </svg>
            <svg v-else-if="toast.kind === ToastKind.WARNING" aria-hidden="true" fill="currentColor" height="1em" viewBox="0 0 16 16" width="1em" xmlns="http://www.w3.org/2000/svg">
              <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0M7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.553.553 0 0 1-1.1 0z" />
            </svg>
            <svg v-else-if="toast.kind === ToastKind.DANGER" aria-hidden="true" fill="currentColor" height="1em" viewBox="0 0 16 16" width="1em" xmlns="http://www.w3.org/2000/svg">
              <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0M7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.553.553 0 0 1-1.1 0z" />
            </svg>
            <svg v-else aria-hidden="true" fill="currentColor" height="1em" viewBox="0 0 16 16" width="1em" xmlns="http://www.w3.org/2000/svg">
              <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0" />
            </svg>
          </div>
        </div>
        <div class="message">
          <div class="content" v-html="toast.description" />
        </div>
        <div class="actions">
          <button class="btn default" type="button" @click.exact="close" @click.alt="closeAll">
            {{ $t('SHOP.COMMON.LABEL.OK') }}
          </button>
        </div>
      </div>
    </div>
  </Toast>
</template>
<style lang="scss" scoped>
  .toast-container {
    --duration: v-bind(duration);

    position: relative;
    display: flex;
    flex: 1 1 auto;
    align-items: flex-start;
    box-shadow: 0 0 2px 0 rgb(0 0 0 / 0.25), 0 25px 50px -12px rgb(0 0 0 / 0.32);
    background-color: #171717;
    background-image: linear-gradient(to left, #171717 50%, #0a0a0a 50%, #0a0a0a 100%);
    background-repeat: no-repeat;
    background-size: 200% 100%;
    background-position: 100% 0;
    border-radius: 10px;
    padding: 6px;

    &.active {
      animation: progress linear var(--duration) forwards;
    }
  }

  .toast {
    display: flex;
    flex: 1 1 auto;
    align-items: flex-start;
    font-weight: 400;
    font-size: 12px;
    color: #e5e5e5;
    gap: 12px;

    .icon {
      display: flex;
      flex: 0 0 22px;
      aspect-ratio: 1 / 1;
      border: 1px solid #404040;
      border-radius: 50%;
      font-size: 18px;
      padding: 2px;

      .inside {
        display: flex;
        aspect-ratio: 1 / 1;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        color: #0a0a0a !important;
      }
    }

    .message {
      display: flex;
      align-items: center;
      flex: 1 1 auto;
      min-width: 0;
      max-width: 100%;
      min-height: 24px;
      line-height: 12px;
    }

    .actions {
      display: flex;
      flex: 0 0 auto;
      justify-content: flex-end;
      margin-left: auto;
      gap: 4px;
    }

    .btn {
      padding: 4px 20px;
      font-size: 12px;
      font-weight: 600;
      line-height: 16px;
      border-radius: 6px;
      background-color: #5a5a5a;
      border: none;
      color: #e5e5e5;

      @media (hover: hover) {
        &:hover {
          cursor: pointer;
          background-color: #404040;
          color: #e5e5e5;
        }
      }
    }

    &.success {
      .icon {
        border-color: #16a34a;

        .inside {
          background-color: #16a34a;
        }
      }
    }

    &.warning {
      .icon {
        border-color: #eab308;

        .inside {
          background-color: #eab308;
        }
      }
    }

    &.danger {
      .icon {
        border-color: #ef4444;

        .inside {
          background-color: #ef4444;
        }
      }
    }

    &.info {
      .icon {
        border-color: #3b82f6;

        .inside {
          background-color: #3b82f6;
        }
      }
    }
  }

  @keyframes progress {
    0% {
      background-position: 100% 0;
    }
    100% {
      background-position: 0 0;
    }
  }
</style>
